import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildEngine } from '../../engine/Engine';
import LocationSelector from '../LocationSelector';
import SearchContext from '../../state/context';
import GoogleMapResultsDisplay from '../GoogleMapResultsDisplay';
import ResultsColumn from './ResultsColumn';
import ShareModal from '../ShareModal';
import CompareBar from './CompareBar';
import useEventBus, { dispatch } from '../../../hooks/useEventBus';
import { bindUrlManager } from '../../urlManager';
import { getLocationCookie, setLocationCookie,  getCoveoFindACommunityFieldsToInclude, sendCoveoGeolocationQuery, sendCoveoGeolocationInterfaceLoad, checkForHiddenQueryInHash } from '../../helpers';
import QuerySummary from './QuerySummary';
import { buildQuerySummary, buildPager, buildFacet, buildResultList, loadFacetSetActions, loadSearchActions, loadSearchAnalyticsActions } from '@coveo/headless';
import { Pager } from '../../components/Pager';
import DropdownFacet from '../../components/DropdownFacet';
import DropdownFacetWithCustomOptions from '../../components/DropdownFacetWithCustomOptions';
import Module from '../../../components/location-selector';

const FindACommunityMapViewRow = props => {
  const {
    shareResultsBtnLabel,
    shareModalCopy,
    shareModalTitle,
    compareBtnLabel,
    clearBtnLabel,
    shareBtnLabel,
    compareUrlPath,
    module,
    copyBtnLabel,
    mapApiKey,
    locationCookieName,
    coveoOrganizationId,
    coveoAccessToken,
    coveoSearchHub,
    coveoPipeline,
    coveoAutocompleteFields,
    coveoSearchRadiusInMiles = 50,
    coveoSource = 'Production US - English',
    coveoContextLanguage = 'en',
    coveoQuerySummaryShowingText = 'Showing',
    coveoQuerySummaryOfText = 'of',
    coveoQuerySummaryResultsText = 'results',
    clearFiltersLabel = 'Clear Filters',
    applyFiltersLabel = 'Apply Filters',
    noResultsMessage,
    locationSelectorOptions,
    searchButtonLabel,
    mastheadHeading = 'Find a Community',
    mastheadCopy = 'Find a community near you by entering your zip code or city and state below.',
    theme = 'dark',
    distanceOptions = [],
    careTypeField = '',
    stateToFilterBy = null,
    careTypeDropdownLabel = 'Levels of Care'
  } = props;

  
  useEffect(() => {
    const params = window.location.search.slice(1);
    const data = JSON.parse('{"' + params.replace(/&/g, '","').replace(/=/g,'":"') + '"}', function(key, value) { return key===""?value:decodeURIComponent(value) });
  
    setLocationCookie(data);
    
    setupPage();
  }, []);

  const querySummaryObject = useMemo(() => {
    return {
      querySummaryShowingText: coveoQuerySummaryShowingText,
      querySummaryOfText: coveoQuerySummaryOfText,
      querySummaryResultsText: coveoQuerySummaryResultsText
    };
  }, [ coveoQuerySummaryShowingText, coveoQuerySummaryOfText, coveoQuerySummaryResultsText ]);

  const [selectedFacetsPerField, setSelectedFacetsPerField] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [isMapActive, setIsMapActive] = useState(false);
  const [comparedCommunities, setComparedCommunities] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [waitingForGeolocation, setWaitingForGeolocation] = useState(true);
  const [geolocationData, setGeolocationData] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState([]);
  const [facetReset, setFacetReset] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [shareUrl, setShareUrl] = useState('');
  const isSearching = useRef(false);
  const firstLoad = useRef(true);

  const searchSource = useRef(
    JSON.parse(locationSelectorOptions).find(option => option.defaultValue === 'true').source || coveoSource
  );

  const engine = useMemo(() => {
    return buildEngine({
      organizationId: coveoOrganizationId,
      accessToken: coveoAccessToken,
      searchHub: coveoSearchHub,
      pipeline: coveoPipeline
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const querySummaryController = useMemo(() => buildQuerySummary(engine), []);
  const advancedQuery = useRef(null);
  const resultsListController = useMemo(
    () =>
      buildResultList(engine, {
        options: {
          fieldsToInclude: getCoveoFindACommunityFieldsToInclude()
        }
      }),
    []
  );

  const levelsOfCareFacetController = useMemo(
    () =>
      buildFacet(engine, {
        options: {
          field: careTypeField,
          facetId: 'ssl_levelsofcare_01'
        }
      }),
    []
  );

  const pagerController = useMemo(() => buildPager(engine), []);

  const handleResize = () => {
    setIsMapActive(false);
  };

  const handleLocationSelectorSubmit = (searchInputValue, selectedCountry, selectedCareType, selectedDistance) => {
    isSearching.current = true;

    dispatch({
      type: 'get-zip-geolocation',
      payload: {
        value: searchInputValue,
        country: `${selectedCountry.value === 'us' ? 'USA' : 'CANADA'}`,
        moduleName: module
      }
    });
    setWaitingForGeolocation(true);
  };

  useEventBus(
    'geolocation-received',
    ({ payload }) => {
      if (payload.Module === module) {
        sendQuery();
      } else {
        return;
      }
    },
    []
  );

  useEffect(() => {
    advancedQuery.current = checkForHiddenQueryInHash();
    if (stateToFilterBy !== null && stateToFilterBy !== '') {
      advancedQuery.current = advancedQuery.current ? `${advancedQuery.current} @ssl_state=="${stateToFilterBy}"` : `(@ssl_state=="${stateToFilterBy}")`;
    }

    const unsubscribeUrlManager = bindUrlManager(engine);
    
    levelsOfCareFacetController.subscribe(() => {
      const selecteedLevelFacets = levelsOfCareFacetController.state.values.filter(v => v.state === 'selected');
      if (selecteedLevelFacets.length > 0) {
        setFiltersApplied(selecteedLevelFacets);
      }
    });
    
    return () => unsubscribeUrlManager();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    
    handleResize();
    
    return () =>  window.removeEventListener('resize', handleResize);
  }, []);

  const setupPage = () => {
    let cookieData = getLocationCookie(locationCookieName);

    if (cookieData) {
      const state = !!cookieData.LastSearchValue && cookieData.LastSearchValue.length > 0 && !!cookieData.State ? cookieData.State : null;
      const radius = !!cookieData.LastSearchValue && cookieData.LastSearchValue.length > 0 && cookieData.State ? null : parseFloat(coveoSearchRadiusInMiles);

      setGeolocationData(cookieData);
      
      sendCoveoGeolocationInterfaceLoad({
        engine: engine,
        latitude: cookieData.Latitude,
        longitude: cookieData.Longitude,
        advancedUrlQuery: advancedQuery.current,
        source: cookieData.Source || coveoSource,
        state: state,
        radiusInMiles: radius,
        language: coveoContextLanguage,
        pipeline: coveoPipeline,
        previousLoad: !firstLoad.current
      });
    }

    firstLoad.current = false;
    setWaitingForGeolocation(false);
  };

  const sendQuery = () => {
    let cookieData = getLocationCookie(locationCookieName);
    
    if (cookieData) {
      setGeolocationData(cookieData);
      sendCoveoGeolocationQuery({
        engine: engine,
        latitude: cookieData.Latitude,
        longitude: cookieData.Longitude,
        source: cookieData.Source || coveoSource,
        radiusInMiles: parseFloat(coveoSearchRadiusInMiles),
        state: cookieData.State,
        language: coveoContextLanguage,
        pipeline: coveoPipeline
      });
    }
    
    requestAnimationFrame(() => {
      setWaitingForGeolocation(false);
    });
  };

  const handleCountryChange = selected => {
    searchSource.current = JSON.parse(locationSelectorOptions).find(option => option.value === selected.value).source;
  };

  const handleCustomFacetChange = (facet, selectedValues, immediateAction = true) => {
    if (facet.id === 'distance' && immediateAction) {
      sendCoveoGeolocationQuery({
        engine: engine,
        latitude: geolocationData.Latitude,
        longitude: geolocationData.Longitude,
        source: geolocationData.source || coveoSource,
        state: null,
        radiusInMiles: selectedValues[0]
          ? parseFloat(selectedValues[0].value)
          : parseFloat(coveoSearchRadiusInMiles),
        language: coveoContextLanguage,
        pipeline: coveoPipeline
      });
    } else {
      setSelectedFacetsPerField(prevSelectedFacetsPerField => {
        const newSelectedFacetsPerField = { ...prevSelectedFacetsPerField };
        newSelectedFacetsPerField[facet.id] = selectedValues;
        return newSelectedFacetsPerField;
      });
    }
    setFiltersApplied(selectedValues);
  };

  const handleCoveoFacetChange = (facet, selectedValues) => {
    if (facet) {
      const facetId = facet.id;
      const newSelectedFacetsPerField = { ...selectedFacetsPerField };
      newSelectedFacetsPerField[facetId] = selectedValues;
      setSelectedFacetsPerField(newSelectedFacetsPerField);
    }
  };

  const handleCloseCommunity = result => {
    if (result === 'all') {
      setComparedCommunities([]);
      return;
    }
    setComparedCommunities(prevCommunities => prevCommunities.filter(c => c.raw.ssl_communityid !== result.raw.ssl_communityid));
  };

  const handleApplyFilters = () => {
    const { logSearchboxSubmit } = loadSearchAnalyticsActions(engine);
    const { executeSearch } = loadSearchActions(engine);
    const { toggleSelectFacetValue, deselectAllFacetValues } = loadFacetSetActions(engine);

    engine.dispatch(deselectAllFacetValues('ssl_levelsofcare_01'));

    let cookieData = getLocationCookie(locationCookieName);

    sendCoveoGeolocationQuery({
      engine: engine,
      latitude: cookieData.Latitude,
      longitude: cookieData.Longitude,
      source: searchSource.current,
      radiusInMiles: typeof selectedFacetsPerField?.distance !== 'undefined' ? parseFloat(selectedFacetsPerField?.distance[0].value) : null,
      state: cookieData.State,
      language: coveoContextLanguage,
      pipeline: coveoPipeline
    });

    selectedFacetsPerField.ssl_levelsofcare_01.map(facet => {
      engine.dispatch(
        toggleSelectFacetValue({
          facetId: 'ssl_levelsofcare_01',
          selection: {
            value: facet.value,
            state: 'selected',
            numberOfResults: facet.numberOfResults
          }
        })
      );
    });

    engine.dispatch(executeSearch(logSearchboxSubmit()));
  };

  const handleCompareCommunity = result => {
    const index = comparedCommunities.findIndex(c => c.raw.ssl_communityid === result.raw.ssl_communityid);

    if (comparedCommunities.length <= 3 && index === -1) {
      if (index === -1) {
        setComparedCommunities(prevCommunities => [...prevCommunities, result]);
      } else {
        setComparedCommunities(prevCommunities =>
          prevCommunities.filter(
            c => c.raw.ssl_communityid !== result.raw.ssl_communityid
          )
        );
      }
    } else {
      return false;
    }
  };

  const handleClearFilters = () => {
    levelsOfCareFacetController.deselectAll();
    handleCustomFacetChange({ id: 'distance' }, 50);
    setFacetReset(true);
    setTimeout(() => {
      setFiltersApplied([]);
    }, 1000);
  };

  const handleMapSwitcherClick = mapActive => {
    setIsMapActive(mapActive);
  };

  return (
    <SearchContext.Provider
      value={{
        resultsListController: resultsListController,
        expanded: expanded,
        comparedCommunities: comparedCommunities,
        waitingForGeolocation: waitingForGeolocation,
        geolocationData: geolocationData,
        querySummaryObject: querySummaryObject,
        onCompareClick: handleCompareCommunity,
        onMapSwitcherClick: handleMapSwitcherClick,
        onShareModalClick: url => {
          setShareUrl(url);
          setShowShareModal(!showShareModal);
        },
        isMapActive: isMapActive
      }}
    >
      {engine && (
        <section className="find-a-community-masthead" data-theme={theme}>
          <div className="container">
            <div className="masthead-common-content">
              <h1 className="masthead-common-content__heading">
                {mastheadHeading}
              </h1>
              <div className="masthead-common-content__wrapper">
                {mastheadCopy}
              </div>
            </div>
            <div className="find-a-community-heading__search-bar">
              <div className="location-selector-headless location-selector-headless--no-padding">
                <LocationSelector
                  removeLastSearchValue={stateToFilterBy ? true : false}
                  module={module}
                  locationCookieName={locationCookieName}
                  coveoAutocompleteFields={coveoAutocompleteFields}
                  coveoEngine={engine}
                  isAutocomplete={true}
                  searchNoInfoRedirect={null}
                  searchWithInfoRedirect={null}
                  countryOptions={locationSelectorOptions}
                  searchButtonLabel={searchButtonLabel}
                  onCountryChange={handleCountryChange}
                  externalSubmitHandler={handleLocationSelectorSubmit}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      <>
        <section className="find-a-community-map-view-row-headless__coveo-main-section container">
          <div className="find-a-community-map-view-row-headless__facets-and-results-container">
            <div className="find-a-community-map-view-row-headless__coveo-results-column">
              <div className="find-a-community-map-view-row-headless__coveo-results-header">
                <div className="find-a-community-map-view-row__coveo-summary-section">
                  <QuerySummary controller={querySummaryController} />
                  <div>
                    <button
                      className="link find-a-community-map-view-row__clear-btn"
                      type="button"
                      disabled={filtersApplied.length === 0}
                      onClick={() => {
                        handleClearFilters();
                      }}
                    >
                      <span className="link__label">{clearFiltersLabel}</span>
                    </button>
                  </div>
                </div>
                <div className="find-a-community-map-view-row__mobile-filters-hide-show-section">
                  {filtersApplied.length > 0 && (
                    <span className="find-a-community-map-view-row__mobile-filters-info">
                      {filtersApplied.length} applied
                    </span>
                  )}
                  <div>
                    <button
                      className="link find-a-community-map-view-row__clear-btn"
                      type="button"
                      disabled={filtersApplied.length === 0}
                      onClick={() => {
                        handleClearFilters();
                      }}
                    >
                      <span className="link__label">{clearFiltersLabel}</span>
                    </button>
                  </div>
                </div>
                <div className="find-a-community-map-view-row__filters-and-share-section">
                  <div className="find-a-community-map-view-row-headless__mobile-filters-section">
                    {isMobile ? (
                      <>
                        <DropdownFacet
                          controller={levelsOfCareFacetController}
                          facet={{
                            id: 'ssl_levelsofcare_01',
                            title: careTypeDropdownLabel,
                            isSingleSelect: false
                          }}
                          type={'with-underline-on-select'}
                          showNumberSelected={true}
                          onFacetChange={handleCoveoFacetChange}
                          onResetFacet={facetReset}
                          immediateAction={false}
                        />

                        {distanceOptions !== '' && (
                          <DropdownFacetWithCustomOptions
                            type={'with-underline-on-select'}
                            facet={{
                              id: 'distance',
                              title: 'Distance',
                              isSingleSelect: true
                            }}
                            options={JSON.parse(distanceOptions)}
                            onFacetChange={handleCustomFacetChange}
                            onResetFacet={facetReset}
                            immediateAction={false}
                            values={[{ label: "50 Miles", value: "50"}]}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <DropdownFacet
                          controller={levelsOfCareFacetController}
                          facet={{
                            id: 'ssl_levelsofcare_01',
                            title: careTypeDropdownLabel,
                            isSingleSelect: false
                          }}
                          type={'with-underline-on-select'}
                          immediateAction={true}
                          showNumberSelected={true}
                          onResetFacet={facetReset}
                        />

                        {distanceOptions !== '' && (
                          <DropdownFacetWithCustomOptions
                            type={'with-underline-on-select'}
                            facet={{
                              id: 'distance',
                              title: 'Distance',
                              isSingleSelect: true
                            }}
                            options={JSON.parse(distanceOptions)}
                            onFacetChange={handleCustomFacetChange}
                            onResetFacet={facetReset}
                            immediateAction={true}
                            values={[{ label: "50 Miles", value: "50"}]}
                          />
                        )}
                      </>
                    )}
                  </div>
                  <button
                    className="btn btn--primary find-a-community-map-view-row-headless__share-btn"
                    type="button"
                    onClick={() => {
                      setShareUrl(window.location.href);
                      setShowShareModal(true);
                    }}
                  >
                    <svg width="25" height="25" role="presentation">
                      <use xlinkHref="#share"></use>
                    </svg>
                    <span className="btn__label">{shareResultsBtnLabel}</span>
                  </button>
                  {showShareModal && (
                    <ShareModal
                      shareModalTitle={shareModalTitle}
                      shareModalCopy={shareModalCopy}
                      onClose={() => setShowShareModal(false)}
                      copyBtnLabel={copyBtnLabel}
                      urlToCopy={shareUrl}
                    />
                  )}
                </div>
                <div className="find-a-community-map-view-row__mobile-apply-filters-section">
                  <button
                    className="btn btn--submit find-a-community-map-view-row__apply-filters-button"
                    type="submit"
                    onClick={handleApplyFilters}
                  >
                    <span className="btn__label">{applyFiltersLabel}</span>
                    <svg width="11" height="18" role="presentation">
                      <use xlinkHref="#arrow-right"></use>
                    </svg>
                  </button>
                </div>
              </div>
              <div
                className={`find-a-community-map-view-row-headless__results-and-map ${
                  isMapActive ? 'map-active' : ''
                }`}
              >
                <div className="find-a-community-map-view-row-headless__coveo-result-list">
                  <div className="coveo-result-list-container coveo-list-layout-container">
                    <ResultsColumn
                      onCloseCommunity={handleCloseCommunity}
                      noResultsMessage={noResultsMessage}
                    />
                  </div>
                </div>
                {geolocationData ? (
                  <div className="find-a-community-map-view-row-headless__map-view">
                    <GoogleMapResultsDisplay
                      apiKey={mapApiKey}
                      center={{
                        lat: parseFloat(geolocationData.Latitude),
                        lng: parseFloat(geolocationData.Longitude)
                      }}
                      zoom={10}
                      radiusInMiles={0}
                      zoomControl={true}
                    />
                  </div>
                ) : (
                  <div className="headless-loader">
                    <div className="headless-loader__inner"></div>
                  </div>
                )}
              </div>
              <div className="find-a-community-map-view-row-headless__coveo-results-footer">
                <Pager
                  className="find-a-community-map-view-row-headless"
                  controller={pagerController}
                  results={resultsListController}
                />
              </div>
            </div>
          </div>
        </section>
        <CompareBar
          compareUrlPath={compareUrlPath}
          shareBtnLabel={shareBtnLabel}
          clearBtnLabel={clearBtnLabel}
          compareBtnLabel={compareBtnLabel}
          setExpanded={setExpanded}
          onCloseCommunity={handleCloseCommunity}
        />
      </>
    </SearchContext.Provider>
  );
};

export default FindACommunityMapViewRow;
